import React, { useState } from 'react';

interface JobEditData {
  job_id?: string;
  title: string;
  category: string;
  industry: string;
  organizational_level: string;
  employment_types: string[]; // ['Full-time', 'Part-time', 'Remote', 'Internship']
  country: string;
  city: string;
}

interface JobEditSidebarProps {
  /**
   * Initial job data (for editing existing job)
   */
  jobData?: Partial<JobEditData>;

  /**
   * Callback when job is saved
   */
  onSave?: (jobData: JobEditData) => void;

  /**
   * Is currently saving
   */
  isSaving?: boolean;

  /**
   * Callback to close the sidebar
   */
  onClose?: () => void;
}

/**
 * JobEditSidebar Component
 * Right sidebar for recruiters to edit job details in chat context
 */
const JobEditSidebar: React.FC<JobEditSidebarProps> = ({
  jobData,
  onSave,
  isSaving = false,
  onClose,
}) => {
  const [formData, setFormData] = useState<JobEditData>({
    job_id: jobData?.job_id,
    title: jobData?.title || '',
    category: jobData?.category || '',
    industry: jobData?.industry || '',
    organizational_level: jobData?.organizational_level || '',
    employment_types: jobData?.employment_types || [],
    country: jobData?.country || '',
    city: jobData?.city || '',
  });

  const employmentTypeOptions = ['Full-time', 'Part-time', 'Remote', 'Internship'];

  /**
   * Handle input change
   */
  const handleInputChange = (field: keyof JobEditData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Toggle employment type
   */
  const toggleEmploymentType = (type: string) => {
    setFormData(prev => ({
      ...prev,
      employment_types: prev.employment_types.includes(type)
        ? prev.employment_types.filter(t => t !== type)
        : [...prev.employment_types, type],
    }));
  };

  /**
   * Remove employment type tag
   */
  const removeEmploymentType = (type: string) => {
    setFormData(prev => ({
      ...prev,
      employment_types: prev.employment_types.filter(t => t !== type),
    }));
  };

  /**
   * Handle save
   */
  const handleSave = () => {
    if (onSave) {
      onSave(formData);
    }
  };

  /**
   * Check if form is valid
   */
  const isFormValid = (): boolean => {
    return !!(
      formData.title.trim() &&
      formData.category.trim() &&
      formData.industry.trim() &&
      formData.organizational_level.trim() &&
      formData.employment_types.length > 0 &&
      formData.country.trim() &&
      formData.city.trim()
    );
  };

  return (
    <div className="w-full h-full bg-white overflow-hidden flex flex-col">
      {/* Header with close button */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB]">
        <h3 className="font-manrope font-semibold text-base text-[#1A1A1A]">Edit Job</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F1F3F4] transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col p-5 gap-5">
          {/* Job Introduction Section */}
          <div className="flex flex-col gap-4">
            {/* Section header */}
            <div className="flex flex-row items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="6" width="16" height="14" rx="2" stroke="#1A1A1A" strokeWidth="2"/>
                <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round"/>
                <path d="M4 10H20" stroke="#1A1A1A" strokeWidth="2"/>
              </svg>
              <h2 className="font-manrope font-semibold text-base text-[#1A1A1A]">Job Introduction</h2>
            </div>

          {/* Job title */}
          <div className="flex flex-col gap-2">
            <label className="font-manrope font-medium text-sm text-[#1A1A1A]">
              Job title<span className="text-[#DC0000]">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="User interface Designer (UI Designer)"
              className="w-full px-4 py-3 font-manrope font-normal text-sm text-[#1A1A1A] bg-white border border-[#EDEDED] rounded-lg focus:outline-none focus:border-[#015FA6] transition-colors"
            />
          </div>

          {/* Job category */}
          <div className="flex flex-col gap-2">
            <label className="font-manrope font-medium text-sm text-[#1A1A1A]">
              Job category<span className="text-[#DC0000]">*</span>
            </label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              placeholder="Please type your job category"
              className="w-full px-4 py-3 font-manrope font-normal text-sm text-[#757575] bg-white border border-[#EDEDED] rounded-lg focus:outline-none focus:border-[#015FA6] transition-colors"
            />
          </div>

          {/* Organization industry */}
          <div className="flex flex-col gap-2">
            <label className="font-manrope font-medium text-sm text-[#1A1A1A]">
              Organization industry<span className="text-[#DC0000]">*</span>
            </label>
            <input
              type="text"
              value={formData.industry}
              onChange={(e) => handleInputChange('industry', e.target.value)}
              placeholder="Please type your organization industry"
              className="w-full px-4 py-3 font-manrope font-normal text-sm text-[#757575] bg-white border border-[#EDEDED] rounded-lg focus:outline-none focus:border-[#015FA6] transition-colors"
            />
          </div>

          {/* Organizational level */}
          <div className="flex flex-col gap-2">
            <label className="font-manrope font-medium text-sm text-[#1A1A1A]">
              Organizational level<span className="text-[#DC0000]">*</span>
            </label>
            <input
              type="text"
              value={formData.organizational_level}
              onChange={(e) => handleInputChange('organizational_level', e.target.value)}
              placeholder="Please type your Organizational level"
              className="w-full px-4 py-3 font-manrope font-normal text-sm text-[#757575] bg-white border border-[#EDEDED] rounded-lg focus:outline-none focus:border-[#015FA6] transition-colors"
            />
          </div>
        </div>

        {/* Employment Type Section */}
        <div className="flex flex-col gap-4 pt-4 border-t border-[#EDEDED]">
          {/* Section header */}
          <div className="flex flex-row items-center gap-2">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="4" width="18" height="18" rx="2" stroke="#1A1A1A" strokeWidth="2"/>
              <path d="M8 2V6M16 2V6M3 10H21" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <h3 className="font-manrope font-bold text-base text-[#1A1A1A]">Employment Type</h3>
          </div>

          {/* Checkboxes */}
          <div className="grid grid-cols-2 gap-3">
            {employmentTypeOptions.map((type) => (
              <label key={type} className="flex flex-row items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.employment_types.includes(type)}
                  onChange={() => toggleEmploymentType(type)}
                  className="w-5 h-5 rounded border-2 border-[#757575] text-[#015FA6] focus:ring-2 focus:ring-[#015FA6] focus:ring-offset-0 cursor-pointer"
                />
                <span className="font-manrope font-normal text-sm text-[#1A1A1A]">{type}</span>
              </label>
            ))}
          </div>

          {/* Selected tags */}
          {formData.employment_types.length > 0 && (
            <div className="flex flex-row flex-wrap gap-2">
              {formData.employment_types.map((type) => (
                <button
                  key={type}
                  onClick={() => removeEmploymentType(type)}
                  className="flex flex-row items-center gap-2 px-3 py-1 bg-[#F1F3F4] rounded-full hover:bg-[#E0E0E0] transition-colors"
                >
                  <span className="font-manrope font-medium text-sm text-[#1A1A1A]">{type}</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 3L3 9M3 3L9 9" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Work Location Section */}
        <div className="flex flex-col gap-4 pt-4 border-t border-[#EDEDED]">
          {/* Section header */}
          <div className="flex flex-row items-center gap-2">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="#1A1A1A" strokeWidth="2"/>
              <path d="M12 2C9.87827 2 7.84344 2.84285 6.34315 4.34315C4.84285 5.84344 4 7.87827 4 10C4 12.892 4.804 15.13 6.5 17.5L12 22L17.5 17.5C19.196 15.13 20 12.892 20 10C20 7.87827 19.1571 5.84344 17.6569 4.34315C16.1566 2.84285 14.1217 2 12 2Z" stroke="#1A1A1A" strokeWidth="2"/>
            </svg>
            <h3 className="font-manrope font-bold text-base text-[#1A1A1A]">Work Location</h3>
          </div>

          {/* Country and City */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <label className="font-manrope font-medium text-sm text-[#1A1A1A]">
                Country<span className="text-[#DC0000]">*</span>
              </label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                placeholder="USA"
                className="w-full px-4 py-3 font-manrope font-normal text-sm text-[#1A1A1A] bg-white border border-[#EDEDED] rounded-lg focus:outline-none focus:border-[#015FA6] transition-colors"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="font-manrope font-medium text-sm text-[#1A1A1A]">
                City<span className="text-[#DC0000]">*</span>
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="New York"
                className="w-full px-4 py-3 font-manrope font-normal text-sm text-[#1A1A1A] bg-white border border-[#EDEDED] rounded-lg focus:outline-none focus:border-[#015FA6] transition-colors"
              />
            </div>
          </div>

          {/* Location tag */}
          {formData.country && formData.city && (
            <div className="flex flex-row flex-wrap gap-2">
              <div className="flex flex-row items-center gap-2 px-3 py-1 bg-[#F1F3F4] rounded-full">
                <span className="font-manrope font-medium text-sm text-[#1A1A1A]">
                  {formData.city}/{formData.country}
                </span>
                <button
                  onClick={() => {
                    handleInputChange('country', '');
                    handleInputChange('city', '');
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 3L3 9M3 3L9 9" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

          {/* Save button */}
          <div className="flex flex-col gap-2 pt-4">
            <button
              onClick={handleSave}
              disabled={!isFormValid() || isSaving}
              className="w-full px-4 py-3 bg-[#015FA6] text-white font-manrope font-semibold text-sm rounded-xl hover:bg-[#014580] active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Job Details'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobEditSidebar;
